// Twitter's Typeahead.js for activating the typeahead functionality into jQuery elements
import 'typeahead.js';
// Bloodhound suggestions engine for prefetching, caching and faster lookups from the data
const Bloodhound = require('typeahead.js/dist/bloodhound.min.js');

/**
 * A class for encapsulating any Twitter's Typeahead.js search functionality in independent instances.
 * @module  ARASearch
 * @author  Javier Colmenárez
 * Below some control and reusable variables
 * @const   {Object}          typeaheadBaseConfig - Basic configuration for Typeahead.js instances, should be extended with proper 'menu' jQuery element
 *      @prop   {Object}      classNames          - Custom CSS class names for every Typeahead.js element and their states
 *      @prop   {Boolean}     hint                - Flag for showing/hiding the suggestion hints in the input field
 * @const   {Object}          templates           - Custom templates cached reference for reuse in all of the search instances
 *      @prop   {Function}    notFound            - No results custom template, rendered only when user typed in and the results are not matching anything
 *      @prop   {Function}    suggestion          - Regular results custom template, rendered for any valid results
 */
const typeaheadBaseConfig = {
  classNames: {
    input: 'TypeAhead-input',
    menu: 'TypeAhead-resultsWrapper',
    empty: 'TypeAhead-resultsWrapper--empty',
    open: 'TypeAhead-resultsWrapper--opened',
    dataset: 'TypeAhead-resultsList',
    suggestion: 'TypeAhead-resultItem',
    selectable: 'TypeAhead-resultItem--selectable',
    cursor: 'TypeAhead-resultItem--focused',
  },
  hint: false,
};
const templates = {
  notFound: evt => {
    return `<li class="TypeAhead-resultItem--empty">No results for <span>"${evt.query}"</span>.<br />Please review it and try again.</li>`
  },
  suggestion: result => {
    return `<li${result.id ? [' data-uid="', result.id,'"'].join('') : ''}>${result.name || result}</li>`
  },
};
/**
 * @class ARASearch
 * @author Javier Colmenárez
 * @const {Symbol}  emptyStateUpdater - A Symbol unique reference to create a private method within the class
 */
const emptyStateUpdater = Symbol('emptyStateUpdater');
class ARASearch {
  /**
   * An instance with a future Bloodhound data set, a specific data config to be extended and the required jQuery elements references
   * @param {Object}  - Destructured configuration object from which the above described members are being set
   */
  constructor ({ dataConfig, $field, $results }){
    this.dataConfig = dataConfig;
    this.dataSet = null;
    this.$field = $field;
    this.$results = $results;
  }
  /**
   * Init method for extending all of the required configuration objects and activating the Typeahead.js plugin
   * @method  init  [public]
   * @param   {Array||String}       data    - A dataset reference, either a local array or an URL string to get the data from
   * @param   {String||Undefined}   dataKey - An optional key name to get the items' value to be rendered. Must be passed if dataset is an arrays of objects
   */
  init (data, dataKey) {
    // Specific Bloodhound configuration to be extended according to the data's type
    const bloodhoundBasicConfig = {
      datumTokenizer: dataKey ?
        Bloodhound.tokenizers.obj.whitespace(dataKey) :
        Bloodhound.tokenizers.whitespace,
      queryTokenizer: Bloodhound.tokenizers.whitespace,
    };
    // Flag for setting up the data source type (either a local data or a remote URL)
    const isUrl = typeof(data) === 'string';
    // Creating a Bloodhound data engine instance for managing the queries to the dataset
    this.dataSet = new Bloodhound({
      ...bloodhoundBasicConfig,
      prefetch: isUrl ? data : undefined,
      local: !isUrl ? data : undefined,
    });
    // Invoking the Twitter's Typeahead.js plugin on the instance's input search field
    // and binding a handler for the keyup and Typeahead's active events for updating the empty results visibility
    this.$field.typeahead(
      {...typeaheadBaseConfig, menu: this.$results},
      {...this.dataConfig, source: this.dataSet, node: this.$results.find('ul'), templates}
    ).on('keyup typeahead:active', this[emptyStateUpdater].bind(this));
  }
  /**
   * Handler for updating the pristine CSS class to the respective results list in order to properly show the empty results layout
   * @method  emptyStateUpdater [private] - Symbol unique reference to make this method private and unaccessible from outside the class
   * @param   {Event}                     - Optional event type object
   */
  [emptyStateUpdater] () {
    // Get the instance's results list and if the input field's value is empty then add the CSS class to hide the empty message,
    // otherwise remove the CSS class in order to show the empty message only when there is some value in the input field.
    this.$results[
      !this.$field.get(0).value.length ?
      'addClass' :
      'removeClass'
    ]('TypeAhead-resultsWrapper--pristine');
  }
}
// Exporting the ARASearch class as the default value for the module
export default ARASearch;



// WEBPACK FOOTER //
// ./scripts/util/araSearch.js